import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {boolean} */
  #isRestoring = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Skip processing if we're currently restoring selection state to prevent infinite loops
    if (this.#isRestoring) return;

    try {
      this.updateSelectedOption(event.target);
      this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));
    } catch (error) {
      console.error('Error in variantChanged:', error);
      // Reset restoration flag in case of error
      this.#isRestoring = false;
      return;
    }

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // Always fetch updates for now - the parameter optimization in buildRequestUrl will handle efficiency
    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);

    let variantId;

    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      variantId = event.target.dataset.variantId || null;
    } else if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    // Check if this is a custom variant picker (direct variant selection)
    const isCustomVariantPicker = this.classList.contains('custom-variant-picker') || 
                                  selectedOption.classList.contains('custom-variant-radio');

    if (isCustomVariantPicker && selectedOption.dataset.variantId) {
      // For custom variant picker, use direct variant parameter
      params.push(`variant=${selectedOption.dataset.variantId}`);
    } else if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        
        try {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          // Defer is only useful for the initial rendering of the page. Remove it here.
          html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

          const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
          if (!textContent) {
            console.warn('No variant data found in response');
            return;
          }

          // Validate JSON before processing
          let variantData;
          try {
            variantData = JSON.parse(textContent);
          } catch (parseError) {
            console.error('Failed to parse variant JSON:', parseError);
            return;
          }

          if (shouldMorphMain) {
            this.updateMain(html);
          } else {
            const newProduct = this.updateVariantPicker(html);

            // We grab the variant object from the response and dispatch an event with it.
            if (this.selectedOptionId) {
              this.dispatchEvent(
                new VariantUpdateEvent(variantData, this.selectedOptionId, {
                  html,
                  productId: this.dataset.productId ?? '',
                  newProduct,
                })
              );
            }
          }
        } catch (error) {
          console.error('Error processing variant update response:', error);
          // Reset any pending states
          this.#isRestoring = false;
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    // Preserve custom variant picker selection state before morphing
    const isCustomVariantPicker = this.classList.contains('custom-variant-picker');
    let selectedVariantId = null;
    
    if (isCustomVariantPicker) {
      try {
        const checkedInput = this.querySelector('.custom-variant-radio:checked');
        if (checkedInput instanceof HTMLInputElement) {
          selectedVariantId = checkedInput.dataset.variantId;
        }
      } catch (error) {
        console.warn('Error preserving variant selection:', error);
      }
    }

    try {
      morph(this, newVariantPickerSource);
    } catch (morphError) {
      console.error('Error morphing variant picker:', morphError);
      // Reset flag in case morph fails
      this.#isRestoring = false;
      throw morphError;
    }

    // Restore selection state for custom variant picker after morphing
    if (isCustomVariantPicker && selectedVariantId) {
      try {
        const targetInput = this.querySelector(`[data-variant-id="${selectedVariantId}"]`);
        if (targetInput instanceof HTMLInputElement) {
          // Set flag to prevent infinite loop during restoration
          this.#isRestoring = true;
          
          targetInput.checked = true;
          // Dispatch change event to update related components
          targetInput.dispatchEvent(new Event('change', { bubbles: true }));
          
          // Reset flag after restoration with a small delay to ensure event processing
          setTimeout(() => {
            this.#isRestoring = false;
          }, 0);
        } else {
          // Reset flag if target not found
          this.#isRestoring = false;
        }
      } catch (restoreError) {
        console.error('Error restoring variant selection:', restoreError);
        // Ensure flag is reset even if restoration fails
        this.#isRestoring = false;
      }
    }

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    try {
      const main = document.querySelector('main');
      const newMain = newHtml.querySelector('main');

      if (!main || !newMain) {
        throw new Error('No main content found for morphing');
      }

      morph(main, newMain);
    } catch (error) {
      console.error('Error updating main content:', error);
      // Reset any pending states
      this.#isRestoring = false;
      throw error;
    }
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }


}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
